import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { Alert, AlertDescription, Button, Input, Label, Textarea, Toggle } from '../../ui';
import { PremiumBrandingAlert } from '../portal/PremiumBrandingAlert';
import { checkIpAllowlist } from '../../../services/api';
import type { IpAllowlistCheckResponse, IpAllowlistEntry } from '../../../types';

type EntryError = { line: number | null; value: string; message: string };

export interface IpAllowlistSaveResult {
  ok: boolean;
  errors?: string[];
  entryErrors?: EntryError[];
  // Entries as stored by the server
  entries?: (string | IpAllowlistEntry)[];
}

interface IpAllowlistSettingsProps {
  enabled: boolean;
  entries: (string | IpAllowlistEntry)[];
  isProCloudPlan: boolean;
  scope: 'shared-guides' | 'help-center';
  onSave: (params: { enabled: boolean; entries: IpAllowlistEntry[] }) => Promise<IpAllowlistSaveResult>;
}

type Row = { value: string; label: string; description?: string };
type Connection = Pick<IpAllowlistCheckResponse, 'ip' | 'family' | 'network' | 'allowed'>;

const MAX_LABEL_LENGTH = 60;

// Lists saved before labels existed are plain strings.
function toRows(entries: (string | IpAllowlistEntry)[] | undefined): Row[] {
  return (entries || []).map((entry) =>
    typeof entry === 'string' ? { value: entry, label: '' } : { value: entry.value, label: entry.label || '' }
  );
}

function rowsKey(rows: Row[]) {
  return JSON.stringify(rows.map((row) => [row.value, row.label.trim()]));
}

export const IpAllowlistSettings: React.FC<IpAllowlistSettingsProps> = ({
  enabled,
  entries,
  isProCloudPlan,
  scope,
  onSave,
}) => {
  const { t } = useTranslation();
  const savedRows = useMemo(() => toRows(entries), [entries]);
  // Keyed on content: callers often pass a fresh `[]`, which would reset the draft every render.
  const savedKey = rowsKey(savedRows);

  const [draftEnabled, setDraftEnabled] = useState(enabled);
  const [rows, setRows] = useState<Row[]>(savedRows);
  const [addValue, setAddValue] = useState('');
  const [addLabel, setAddLabel] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [isPasteOpen, setIsPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteErrors, setPasteErrors] = useState<string[]>([]);
  const [connection, setConnection] = useState<Connection | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveEntryErrors, setSaveEntryErrors] = useState<EntryError[]>([]);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const savedRowsRef = useRef(savedRows);
  savedRowsRef.current = savedRows;

  useEffect(() => {
    setDraftEnabled(enabled);
    // Keep descriptions already fetched; the saved list doesn't carry them.
    setRows((current) =>
      savedRowsRef.current.map((row) => ({
        ...row,
        description: current.find((item) => item.value === row.value)?.description,
      }))
    );
  }, [enabled, savedKey]);

  // Refreshes row descriptions and the "you're connected from" status for the current rows.
  const refresh = useMemo(
    () =>
      debounce(async (values: string[]) => {
        try {
          const response = await checkIpAllowlist(values);
          if (!response.ok) return;
          setConnection({
            ip: response.ip,
            family: response.family,
            network: response.network,
            allowed: response.allowed,
          });
          const descriptions = new Map<string, string>();
          response.entries.forEach((entry, index) => {
            if (entry.description) descriptions.set(values[index], entry.description);
          });
          setRows((current) =>
            current.map((row) =>
              descriptions.has(row.value) ? { ...row, description: descriptions.get(row.value) } : row
            )
          );
        } catch (err) {
          console.error('Error checking IP allowlist:', err);
        }
      }, 300),
    []
  );

  const valuesKey = rows.map((row) => row.value).join('\n');
  useEffect(() => {
    refresh(valuesKey ? valuesKey.split('\n') : []);
  }, [valuesKey, refresh]);

  useEffect(() => () => refresh.cancel(), [refresh]);

  // Validates and normalizes entries on the server, then appends the valid new ones.
  // Returns an error message per input that was not added.
  const addEntries = useCallback(async (inputs: { value: string; label: string }[]) => {
    const response = await checkIpAllowlist(inputs.map((input) => input.value));
    const added: Row[] = [];
    const errors: { input: string; message: string }[] = [];
    response.entries.forEach((entry, index) => {
      const input = inputs[index];
      if (!entry.value) {
        errors.push({ input: input.value.trim(), message: entry.error || t('access.notValidIp') });
      } else if (rowsRef.current.some((row) => row.value === entry.value) || added.some((row) => row.value === entry.value)) {
        errors.push({ input: input.value.trim(), message: t('access.alreadyOnList') });
      } else {
        added.push({ value: entry.value, label: input.label.trim(), description: entry.description });
      }
    });
    if (added.length) setRows((current) => [...current, ...added]);
    return { added: added.length, errors };
  }, []);

  const handleAdd = async () => {
    if (!addValue.trim()) {
      setAddError(t('access.enterAddressOrRange'));
      return;
    }
    try {
      const { errors } = await addEntries([{ value: addValue, label: addLabel }]);
      if (errors.length) {
        setAddError(errors[0].message);
      } else {
        setAddValue('');
        setAddLabel('');
        setAddError(null);
      }
    } catch (err) {
      console.error('Error adding IP allowlist entry:', err);
      setAddError(t('access.couldntCheckAddress'));
    }
  };

  const handlePaste = async () => {
    const lines = pasteText.split(/[\r\n,;]+/).map((line) => line.trim()).filter(Boolean);
    if (!lines.length) {
      setPasteErrors([t('access.pasteAtLeastOne')]);
      return;
    }
    try {
      const { errors } = await addEntries(lines.map((value) => ({ value, label: '' })));
      if (errors.length) {
        setPasteText(errors.map((error) => error.input).join('\n'));
        setPasteErrors(errors.map((error) => `${error.input}: ${error.message}`));
      } else {
        setPasteText('');
        setPasteErrors([]);
        setIsPasteOpen(false);
      }
    } catch (err) {
      console.error('Error adding pasted IP allowlist entries:', err);
      setPasteErrors([t('access.couldntCheckAddresses')]);
    }
  };

  const handleAddConnection = async (value: string) => {
    const { errors } = await addEntries([{ value, label: '' }]);
    if (errors.length) toast.error(errors[0].message, { position: 'top-center', duration: 3000 });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveEntryErrors([]);
    try {
      const result = await onSave({
        enabled: draftEnabled,
        entries: rows.map((row) => ({ value: row.value, label: row.label.trim() })),
      });
      if (result.ok) {
        toast.success(t('access.settingsSaved'), { position: 'top-center', duration: 3000 });
      } else {
        setSaveError(result.errors?.[0] || t('access.couldntSaveSettings'));
        setSaveEntryErrors(result.entryErrors || []);
      }
    } catch (err) {
      console.error('Error saving IP restrictions:', err);
      setSaveError(t('access.couldntSaveSettingsTryAgain'));
    } finally {
      setIsSaving(false);
    }
  };

  const isDirty = draftEnabled !== enabled || rowsKey(rows) !== savedKey;
  // After a lapse the owner can still turn the restriction off (to make content public again),
  // but turning it on or editing it needs a paid plan.
  const isLocked = !isProCloudPlan && !enabled;
  const isLapsed = !isProCloudPlan && enabled;
  const scopeText = scope === 'shared-guides' ? t('access.scopeSharedGuides') : t('access.scopeHelpCenter');

  return (
    <div className="space-y-4">
      {isLocked && <PremiumBrandingAlert context={scope === 'shared-guides' ? 'guide' : 'help-center'} />}

      {isLapsed && (
        <Alert variant="destructive">
          <AlertDescription>
            {t('access.lapsedAlert', {
              scope: scopeText,
              verb: scope === 'shared-guides' ? t('access.verbAre') : t('access.verbIs'),
            })}
          </AlertDescription>
        </Alert>
      )}

      <div className={isLocked ? 'opacity-50 pointer-events-none' : ''}>
        <div className="flex flex-row items-start justify-between gap-6">
          <div className="space-y-0.5">
            <Label htmlFor="ipAllowlistEnabled">{t('access.restrictToNetworks')}</Label>
            <p className="text-[0.8rem] text-muted-foreground">
              {scope === 'shared-guides'
                ? t('access.restrictDescriptionSharedGuides')
                : t('access.restrictDescriptionHelpCenter')}
            </p>
          </div>
          <Toggle
            checked={draftEnabled}
            onChange={() => setDraftEnabled(!draftEnabled)}
            disabled={isLapsed && !draftEnabled}
          />
        </div>

        {draftEnabled && !enabled && scope === 'shared-guides' && (
          <Alert variant="warning" className="mt-3">
            <AlertDescription>{t('access.appliesToAllGuides')}</AlertDescription>
          </Alert>
        )}

        {draftEnabled && (
          <>
            <div className="mt-5 rounded-md border">
              {rows.length === 0 && (
                <p className="px-3 py-4 text-sm text-muted-foreground border-b">
                  {t('access.noNetworksYet')}
                </p>
              )}
              {rows.map((row) => (
                <div
                  key={row.value}
                  className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.9fr)_32px] items-center gap-3 px-3 py-1.5 border-b"
                >
                  <span className="font-mono text-sm truncate" title={row.value}>{row.value}</span>
                  <input
                    type="text"
                    value={row.label}
                    maxLength={MAX_LABEL_LENGTH}
                    placeholder={t('access.addLabelPlaceholder')}
                    aria-label={t('access.labelFor', { value: row.value })}
                    onChange={(e) =>
                      setRows((current) =>
                        current.map((item) => (item.value === row.value ? { ...item, label: e.target.value } : item))
                      )
                    }
                    className="h-8 w-full rounded border border-transparent bg-transparent px-2 text-sm hover:border-input focus:border-input focus:outline-none"
                  />
                  <span className="text-xs text-muted-foreground truncate">{row.description || ''}</span>
                  <button
                    type="button"
                    aria-label={t('access.removeValue', { value: row.value })}
                    onClick={() => setRows((current) => current.filter((item) => item.value !== row.value))}
                    className="flex h-8 w-8 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_auto] items-start gap-3 px-3 py-3">
                <div>
                  <Input
                    type="text"
                    value={addValue}
                    onChange={(e) => {
                      setAddValue(e.target.value);
                      setAddError(null);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                    placeholder="203.0.113.7"
                    aria-label={t('access.addressOrRange')}
                    spellCheck={false}
                    className={`h-8 font-mono text-sm ${addError ? 'border-destructive' : ''}`}
                  />
                  {addError && <p className="mt-1 text-xs text-destructive">{addError}</p>}
                </div>
                <Input
                  type="text"
                  value={addLabel}
                  maxLength={MAX_LABEL_LENGTH}
                  onChange={(e) => setAddLabel(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  placeholder={t('access.labelOptional')}
                  aria-label={t('access.label')}
                  className="h-8 text-sm"
                />
                <Button variant="outline" size="xs" className="h-8" onClick={handleAdd}>
                  <Plus size={14} className="mr-1" />
                  {t('common.add')}
                </Button>
              </div>
            </div>
            <p className="mt-1.5 text-[0.8rem] text-muted-foreground">
              {t('access.addressHint')}{' '}
              {!isPasteOpen && (
                <button type="button" className="text-primary underline-offset-4 hover:underline" onClick={() => setIsPasteOpen(true)}>
                  {t('access.pasteAList')}
                </button>
              )}
            </p>

            {isPasteOpen && (
              <div className="mt-3 rounded-md border p-3">
                <Label htmlFor="ipAllowlistPaste">{t('access.pasteAddresses')}</Label>
                <Textarea
                  id="ipAllowlistPaste"
                  value={pasteText}
                  onChange={(e) => {
                    setPasteText(e.target.value);
                    setPasteErrors([]);
                  }}
                  placeholder={'203.0.113.7\n198.51.100.0/24'}
                  rows={4}
                  spellCheck={false}
                  className="mt-1 font-mono text-sm"
                />
                <p className="mt-1 text-[0.8rem] text-muted-foreground">{t('access.onePerLine')}</p>
                {pasteErrors.length > 0 && (
                  <ul className="mt-2 list-disc pl-5 text-xs text-destructive">
                    {pasteErrors.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                )}
                <div className="mt-2 flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => {
                      setIsPasteOpen(false);
                      setPasteText('');
                      setPasteErrors([]);
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button variant="outline" size="xs" onClick={handlePaste}>
                    {t('access.addToList')}
                  </Button>
                </div>
              </div>
            )}

            {connection?.ip && (
              <div className="mt-5 rounded-md border px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[0.8rem] text-muted-foreground">{t('access.yourIpAddress')}</p>
                    <p className="flex flex-wrap items-center gap-2 font-mono text-sm break-all">
                      {connection.ip}
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 font-sans text-[11px] text-muted-foreground">
                        {connection.family === 'ipv6' ? 'IPv6' : 'IPv4'}
                      </span>
                    </p>
                  </div>
                  {connection.allowed ? (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
                      <Check size={12} />
                      {t('access.onTheList')}
                    </span>
                  ) : connection.network ? (
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <Button variant="outline" size="xs" onClick={() => handleAddConnection(connection.network as string)}>
                        {t('access.addMyNetwork')}
                      </Button>
                      <Button variant="ghost" size="xs" onClick={() => handleAddConnection(connection.ip as string)}>
                        {t('access.addThisAddressOnly')}
                      </Button>
                    </div>
                  ) : (
                    <Button variant="outline" size="xs" className="shrink-0" onClick={() => handleAddConnection(connection.ip as string)}>
                      {t('access.addThisAddress')}
                    </Button>
                  )}
                </div>
                {!connection.allowed && (
                  <p className="mt-3 flex items-start gap-1.5 border-t pt-3 text-[0.8rem] text-amber-700">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>
                      {t('access.notOnListWarning', { scope: scopeText })}
                      {connection.network &&
                        t('access.ipv6Hint', { network: connection.network })}
                    </span>
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {saveError && (
          <div className="mt-3 rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
            <p>{saveError}</p>
            {saveEntryErrors.length > 0 && (
              <ul className="mt-1 list-disc pl-5">
                {saveEntryErrors.map((entryError, index) => (
                  <li key={index}>
                    {entryError.value ? <span className="font-mono">{entryError.value}</span> : null}
                    {entryError.value ? ': ' : ''}
                    {entryError.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {(draftEnabled || isDirty) && (
          <div className="mt-5 flex items-center justify-end gap-3">
            {isDirty && <span className="text-xs text-muted-foreground">{t('access.unsavedChanges')}</span>}
            <Button size="sm" onClick={handleSave} disabled={!isDirty || isSaving}>
              {isSaving ? t('common.saving') : t('access.save')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default IpAllowlistSettings;
